// store/slices/paymentSlice.ts
import {
  CreatePaymentPayload,
  ListPaymentsParams,
  Payment,
  paymentApi,
} from "@/services/paymentApi";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface PaymentState {
  payments: Payment[];
  selectedPayment: Payment | null;
  pagination: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
    itemsPerPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  loading: boolean;
  updating: boolean;
  error: string | null;
}

const initialState: PaymentState = {
  payments: [],
  selectedPayment: null,
  pagination: {
    totalItems: 0,
    totalPages: 0,
    currentPage: 1,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
  loading: false,
  updating: false,
  error: null,
};

// Thunks
export const fetchPayments = createAsyncThunk(
  "payments/fetchAll",
  async (params: ListPaymentsParams) => {
    const response = await paymentApi.list(params);
    return response;
  },
);

export const fetchPaymentById = createAsyncThunk(
  "payments/fetchById",
  async (id: number) => {
    const response = await paymentApi.getById(id);
    return response.payment;
  },
);

export const createPayment = createAsyncThunk(
  "payments/create",
  async (data: CreatePaymentPayload) => {
    const response = await paymentApi.create(data);
    return response.payment;
  },
);

export const deletePayment = createAsyncThunk(
  "payments/delete",
  async (id: number) => {
    await paymentApi.delete(id);
    return id;
  },
);

const paymentSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    clearPaymentError: (state) => {
      state.error = null;
    },
    resetSelectedPayment: (state) => {
      state.selectedPayment = null;
    },
    resetPagination: (state) => {
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.payments = action.payload.payments;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch payments";
      })

      // Fetch by id
      .addCase(fetchPaymentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPayment = action.payload;
      })
      .addCase(fetchPaymentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch payment";
      })

      // Create
      .addCase(createPayment.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.updating = false;
        state.payments.unshift(action.payload);
        // optionally update pagination count
      })
      .addCase(createPayment.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to create payment";
      })

      // Delete
      .addCase(deletePayment.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.updating = false;
        state.payments = state.payments.filter((p) => p.id !== action.payload);
        if (state.selectedPayment?.id === action.payload) {
          state.selectedPayment = null;
        }
      })
      .addCase(deletePayment.rejected, (state, action) => {
        state.updating = false;
        state.error = action.error.message || "Failed to delete payment";
      });
  },
});

export const { clearPaymentError, resetSelectedPayment, resetPagination } =
  paymentSlice.actions;
export default paymentSlice.reducer;
